/**
 * English-specific Card extension types.
 *
 * Composition mixins (per plan §English deck shape):
 *   type EnglishCardExt = EnglishCore & Partial<VerbExt> & Partial<NounExt>
 *                                     & Partial<StressExt> & Partial<CollocationExt>
 *                                     & Partial<SynonymExt>
 *
 * Quiz modes type-narrow on field presence (e.g. `if (card.inflections)`).
 * Optional extensions are chunk-loaded on demand to keep eager bundle
 * ~300-500KB per level (vs ~2MB if everything was eager).
 */

import type { Card } from '@kevvykevwin/lang-core/types';
import type { L1Code, WordTranslation } from './i18n/types';

export type CEFRLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
export type EnglishLevel = CEFRLevel | 'Everyday';

export const ENGLISH_LEVELS: readonly EnglishLevel[] = [
  'A1',
  'A2',
  'B1',
  'B2',
  'C1',
  'C2',
  'Everyday'
] as const;

export type EnglishPos =
  | 'noun'
  | 'verb'
  | 'adj'
  | 'adv'
  | 'prep'
  | 'phrase'
  | 'idiom'
  | 'phrasal-verb';

// ============================================
// Core fields (always present)
// ============================================

export interface EnglishCore {
  /** IPA pronunciation, e.g. "/ˈæp.əl/". From CMUdict→IPA conversion or Wiktionary. */
  ipa?: string;
  audio?: { us?: string; gb?: string };
  /**
   * Sentence-level example. REQUIRED — every card must have one (research:
   * isolated word ↔ translation cards are the anti-pattern in 2026; every
   * major ESL app anchors items to a sentence).
   */
  example: {
    sentence: string;
    sourceUrl: string;
    authorAttribution?: string;
  };
  cefr: CEFRLevel;
  /** NGSL/NAWL frequency rank — drives auto-CEFR fallback when no manual override. */
  freq?: number;
  pos: EnglishPos;
  tags?: string[];
}

// ============================================
// Mixin extensions (optional, per-pos)
// ============================================

export interface VerbExt {
  inflections: {
    pastSimple: string;
    pastParticiple: string;
    presentParticiple: string;
    thirdPerson: string;
  };
}

export interface NounExt {
  inflections: { plural?: string };
}

export interface StressExt {
  syllables: string[];
  /** 0-indexed stressed syllable position. */
  stressIdx: number;
}

export interface CollocationExt {
  /** Common collocates (e.g. for "rain": ["heavy", "light", "pouring"]). */
  collocations: string[];
}

export interface SynonymExt {
  synonyms: string[];
}

export type WordTranslationsExt = {
  wordTranslations?: Partial<Record<L1Code, WordTranslation>>;
};

// ============================================
// Combined extension type
// ============================================

export type EnglishCardExt = EnglishCore &
  Partial<VerbExt> &
  Partial<NounExt> &
  Partial<StressExt> &
  Partial<CollocationExt> &
  Partial<SynonymExt> &
  Partial<WordTranslationsExt>;

export type EnglishCard = Card<EnglishCardExt>;

// ============================================
// Type guards
// ============================================

export function isVerb(card: EnglishCard): card is EnglishCard & VerbExt {
  return Boolean(card.inflections?.pastSimple && card.pos === 'verb');
}

export function hasStress(card: EnglishCard): card is EnglishCard & StressExt {
  return Boolean(card.syllables && card.stressIdx !== undefined);
}

export function hasCollocations(
  card: EnglishCard
): card is EnglishCard & CollocationExt {
  return Boolean(card.collocations && card.collocations.length > 0);
}
